import React, { useState, useContext } from 'react'
import { FormattedMessage } from 'react-intl'

import Input from 'components/Input'
import Row from 'components/Row'
import Col from 'components/Col'
import Container from 'components/Container'
import Select from 'components/Select'
import Modal from 'components/Modal'
import capitalizeString from 'utils/capitalize'
import { INDIVIDUAL_ROLE_ID, NCG_ROLE_ID } from 'constants/roles'
import MultiSelectionDropdown from 'components/multiSelectDropdown'

import ChangePasswordModalForm from './ChangePasswordModalForm'
import ChangeEmailModalForm from './ChangeEmailModalForm'
import ProfileIcon from './ProfileIcon'
import { ChangeButton, YellowStatusDot, PendingEmailChange, PendingChangeRoleIconWrapper } from '../style'
import { renderStatusIcon } from '../../UsersManagement/userTable'
import { CountriesContext } from 'providers/countries'

const ProfileDataForm = ({
  formattedCountries,
  formattedKbaPartners,
  rolesAvailableForRegistration,
  firstName,
  lastName,
  organisation,
  countryCode,
  countryOfInterest,
  kbaPartner,
  selectedRole,
  error,
  profileData,
  formDisabled,
  handleFirstNameChange,
  handleLastNameChange,
  handleCountryCodeChange,
  handleCountryOfInterestChange,
  handleKbaPartnerChange,
  handleOrganisationChange,
  handleRoleChange
}) => {
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const isAbleToChangeRole = INDIVIDUAL_ROLE_ID === profileData.Role.id || NCG_ROLE_ID === profileData.Role.id
  const { countriesOptions } = useContext(CountriesContext)

  return (
    <>
      <Container>
        <Row>
          <Col md={8} mdOffset={2}>
            <div style={{ marginBottom: '64px' }}>
              <ProfileIcon user={profileData} />
            </div>
          </Col>
        </Row>
        <Row>
          <Col md={4} mdOffset={2}>
            <Input
              label="firstName"
              field="firstName"
              disabled
              onChange={handleFirstNameChange}
              value={capitalizeString(firstName)}
              placeholder="firstName"
              dataTest="form-field"
              margin="0 0 32px 0"
            />
          </Col>
          <Col md={4}>
            <Input
              label="lastName"
              disabled
              value={capitalizeString(lastName)}
              onChange={handleLastNameChange}
              placeholder="lastName"
              dataTest="form-field"
              field="lastName"
              margin="0 0 32px 0"
            />
          </Col>
        </Row>
        <Row>
          <Col md={4} mdOffset={2}>
            <Input
              label="organisationLabel"
              disabled={formDisabled}
              field="organisation"
              value={organisation}
              onChange={handleOrganisationChange}
              height="48px"
              placeholder="organisationPlaceholder"
              dataTest="form-field"
              error={error}
            />
          </Col>
          <Col md={4}>
            <Select
              label="countryLabel"
              disabled={formDisabled || NCG_ROLE_ID === profileData.Role.id}
              field="country"
              value={countryCode}
              options={formattedCountries}
              onChange={handleCountryCodeChange}
              height="48px"
              placeholder="countryPlaceholder"
              menuPlacement="bottom"
              dataTest="form-field"
            />
          </Col>
        </Row>
        <Row>
          <Col md={4} mdOffset={2}>
            <Select
              disabled={profileData.RequestedRoleId || formDisabled ? true : !isAbleToChangeRole}
              label="roleSelectLabel"
              field="role"
              value={selectedRole}
              options={rolesAvailableForRegistration}
              onChange={handleRoleChange}
              height="48px"
              placeholder="roleSelectPlaceholder"
              menuPlacement="bottom"
              dataTest="form-field"
            />
            {profileData.RequestedRoleId && (
              <div style={{ position: 'relative' }}>
                <PendingChangeRoleIconWrapper>
                  {renderStatusIcon('pending')}
                  <FormattedMessage id="pending" />
                </PendingChangeRoleIconWrapper>
              </div>
            )}
          </Col>
          <Col md={4}>
            <MultiSelectionDropdown
              marginBottomLabel="16px"
              marginBottom="32px"
              label="countryOfInterestLabel"
              disabled={formDisabled}
              field="countryOfInterest"
              error={error}
              secondStepOptions={countriesOptions}
              value={countryOfInterest}
              saveChanges={handleCountryOfInterestChange}
              infoTooltip="countryHint"
              dataTest='form-field'
            />
          </Col>
        </Row>
        <Row>
          <Col md={4} mdOffset={2}>
            <MultiSelectionDropdown
              marginBottomLabel="16px"
              marginBottom="32px"
              label="kbaPartnerLabel"
              field="kbaPartner"
              secondStepOptions={formattedKbaPartners}
              value={kbaPartner}
              saveChanges={handleKbaPartnerChange}
              infoTooltip="kbaPartnerHint"
              height="48px"
              isOptional={true}
              disabled={formDisabled}
            />
          </Col>
        </Row>
        <Row>
          <Col md={4} mdOffset={2}>
            <div style={{ display: 'grid' }}>
              <div style={{ position: 'relative' }}>
                <Input
                  disabled
                  field="email"
                  label="email"
                  value={profileData.email}
                  placeholder="email"
                  dataTest="form-field"
                />
                {profileData.isPendingEmailChange ? (
                  <PendingEmailChange>
                    <YellowStatusDot /> <FormattedMessage id="pending" />
                  </PendingEmailChange>
                ) : (
                  <ChangeButton
                    data-test="change-email"
                    disabled={formDisabled}
                    available={!formDisabled}
                    onClick={() => {
                      return setShowEmailModal(true)
                    }}
                  >
                    <FormattedMessage id="changeButton" />
                  </ChangeButton>
                )}
              </div>
            </div>
          </Col>
          <Col md={4}>
            <div style={{ display: 'grid' }}>
              <div style={{ position: 'relative' }}>
                <Input disabled field="password" label="password" placeholder={'******'} dataTest="form-field" />
                <ChangeButton
                  data-test="change-password"
                  disabled={formDisabled}
                  available={!formDisabled}
                  onClick={() => {
                    return setShowPasswordModal(true)
                  }}
                >
                  <FormattedMessage id="changeButton" />
                </ChangeButton>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
      <Modal isOpen={showEmailModal} toggleModal={setShowEmailModal} modalTitleId="changeEmail">
        <ChangeEmailModalForm toggleModal={setShowEmailModal} />
      </Modal>
      <Modal isOpen={showPasswordModal} toggleModal={setShowPasswordModal} modalTitleId="changePassword">
        <ChangePasswordModalForm toggleModal={setShowPasswordModal} />
      </Modal>
    </>
  )
}

export default ProfileDataForm
